/**
 * Video utility functions for extracting metadata
 */

/**
 * Extract video duration from file metadata
 * @param {File} file - Video file object
 * @returns {Promise<number>} Duration in seconds
 */
export const extractVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('video/')) {
      reject(new Error('Invalid file type. Must be a video file.'));
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      if (isFinite(video.duration)) {
        resolve(video.duration);
      } else {
        reject(new Error('Unable to determine video duration'));
      }
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Error loading video metadata'));
    };
    
    video.src = window.URL.createObjectURL(file);
  });
};

/**
 * Extract image dimensions from file metadata
 * @param {File} file - Image file object  
 * @returns {Promise<{width: number, height: number}>} Dimensions in pixels
 */
export const extractImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('Invalid file type. Must be an image file.'));
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      window.URL.revokeObjectURL(img.src);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    
    img.onerror = () => {
      window.URL.revokeObjectURL(img.src);
      reject(new Error('Error loading image metadata'));
    };
    
    img.src = window.URL.createObjectURL(file);
  });
};

/**
 * Extract video dimensions from file metadata
 * @param {File} file - Video file object
 * @returns {Promise<{width: number, height: number}>} Dimensions in pixels
 */
export const extractVideoDimensions = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('video/')) {
      reject(new Error('Invalid file type. Must be a video file.'));
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      if (video.videoWidth && video.videoHeight) {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight
        });
      } else {
        reject(new Error('Unable to determine video dimensions'));
      }
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Error loading video metadata'));
    };
    
    video.src = window.URL.createObjectURL(file);
  });
};

/**
 * Generate thumbnail from image file
 * @param {File} file - Image file object
 * @param {number} maxSize - Maximum size (default: 200px)
 * @returns {Promise<string>} Data URL for thumbnail
 */
export const generateImageThumbnail = (file, maxSize = 200) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('Invalid file type. Must be an image file.'));
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate scaled dimensions
      const { width: scaledWidth, height: scaledHeight } = calculateScaledDimensions(
        img.naturalWidth, 
        img.naturalHeight, 
        maxSize
      );
      
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;
      
      // Draw scaled image
      ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);
      
      // Convert to data URL
      const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
      
      window.URL.revokeObjectURL(img.src);
      resolve(thumbnailUrl);
    };
    
    img.onerror = () => {
      window.URL.revokeObjectURL(img.src);
      reject(new Error('Error generating image thumbnail'));
    };
    
    img.src = window.URL.createObjectURL(file);
  });
};

/**
 * Generate thumbnail from video file
 * @param {File} file - Video file object
 * @param {number} timeSeek - Time to seek in seconds (default: 2)
 * @param {number} maxSize - Maximum size (default: 200px)
 * @returns {Promise<string>} Data URL for thumbnail
 */
export const generateVideoThumbnail = (file, timeSeek = 2, maxSize = 200) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('video/')) {
      reject(new Error('Invalid file type. Must be a video file.'));
      return;
    }

    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    video.addEventListener('loadedmetadata', () => {
      // Seek to specified time or 2 seconds, whichever is less
      const seekTime = Math.min(timeSeek, video.duration - 0.1);
      video.currentTime = seekTime > 0 ? seekTime : 0;
    });
    
    video.addEventListener('seeked', () => {
      // Calculate scaled dimensions
      const { width: scaledWidth, height: scaledHeight } = calculateScaledDimensions(
        video.videoWidth,
        video.videoHeight,
        maxSize
      );
      
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;
      
      // Draw video frame
      ctx.drawImage(video, 0, 0, scaledWidth, scaledHeight);
      
      // Add play overlay
      drawPlayOverlay(ctx, scaledWidth, scaledHeight);
      
      // Convert to data URL
      const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
      
      window.URL.revokeObjectURL(video.src);
      resolve(thumbnailUrl);
    });
    
    video.addEventListener('error', () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Error generating video thumbnail'));
    });
    
    video.src = window.URL.createObjectURL(file);
    video.load();
  });
};

/**
 * Draw play overlay on canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 */
const drawPlayOverlay = (ctx, width, height) => {
  const centerX = width / 2;
  const centerY = height / 2;
  const playSize = Math.min(width, height) * 0.15;
  
  // Draw semi-transparent background circle
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx?.beginPath();
  ctx?.arc(centerX, centerY, playSize, 0, 2 * Math.PI);
  ctx?.fill();
  
  // Draw play triangle
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx?.beginPath();
  const triangleSize = playSize * 0.6;
  ctx?.moveTo(centerX - triangleSize / 3, centerY - triangleSize / 2);
  ctx?.lineTo(centerX - triangleSize / 3, centerY + triangleSize / 2);
  ctx?.lineTo(centerX + triangleSize / 2, centerY);
  ctx?.closePath();
  ctx?.fill();
};

/**
 * Calculate scaled dimensions maintaining aspect ratio
 * @param {number} originalWidth - Original width
 * @param {number} originalHeight - Original height
 * @param {number} maxSize - Maximum size for longest side
 * @returns {{width: number, height: number}} Scaled dimensions
 */
const calculateScaledDimensions = (originalWidth, originalHeight, maxSize) => {
  if (originalWidth <= maxSize && originalHeight <= maxSize) {
    return { width: originalWidth, height: originalHeight };
  }
  
  const aspectRatio = originalWidth / originalHeight;
  
  if (originalWidth > originalHeight) {
    return {
      width: maxSize,
      height: Math.round(maxSize / aspectRatio)
    };
  } else {
    return {
      width: Math.round(maxSize * aspectRatio),
      height: maxSize
    };
  }
};

/**
 * Format duration in seconds to mm:ss format
 * @param {number} durationSeconds - Duration in seconds
 * @returns {string} Formatted duration as mm:ss
 */
export const formatDuration = (durationSeconds) => {
  if (!durationSeconds || !isFinite(durationSeconds)) {
    return '00:00';
  }
  
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = Math.floor(durationSeconds % 60);
  
  return `${minutes?.toString()?.padStart(2, '0')}:${seconds?.toString()?.padStart(2, '0')}`;
};

/**
 * Calculate aspect ratio from width and height
 * @param {number} width - Width in pixels
 * @param {number} height - Height in pixels
 * @returns {number} Aspect ratio (width/height)
 */
export const calculateAspectRatio = (width, height) => {
  if (!width || !height || width <= 0 || height <= 0) {
    return 0;
  }
  return width / height;
};

/**
 * Compare two aspect ratios with tolerance
 * @param {number} ratio1 - First aspect ratio
 * @param {number} ratio2 - Second aspect ratio
 * @param {number} tolerance - Tolerance for comparison (default: 0.01)
 * @returns {boolean} True if ratios match within tolerance
 */
export const aspectRatiosMatch = (ratio1, ratio2, tolerance = 0.01) => {
  if (!ratio1 || !ratio2) return false;
  return Math.abs(ratio1 - ratio2) <= tolerance;
};

/**
 * Check if file is a supported video format
 * @param {File} file - File to check
 * @returns {boolean} True if file is a supported video format
 */
export const isSupportedVideoFormat = (file) => {
  if (!file || !file?.type) return false;
  
  const supportedTypes = [
    'video/mp4',
    'video/quicktime', // MOV
    'video/x-msvideo', // AVI
    'video/x-matroska' // MKV
  ];
  
  return supportedTypes?.includes(file?.type);
};

/**
 * Check if file is a supported image format
 * @param {File} file - File to check
 * @returns {boolean} True if file is a supported image format
 */
export const isSupportedImageFormat = (file) => {
  if (!file || !file?.type) return false;
  
  const supportedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp'
  ];
  
  return supportedTypes?.includes(file?.type);
};

/**
 * Get allowed file types based on placement type
 * @param {string} placementType - The placement type
 * @returns {string[]} Array of allowed MIME types
 */
export const getAllowedFileTypes = (placementType) => {
  if (placementType === 'LED Screen') {
    return [
      'video/mp4',
      'video/quicktime', // MOV
      'video/x-msvideo', // AVI
      'video/x-matroska' // MKV
    ];
  }
  
  // For all other placement types, allow images and videos
  return [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/webp',
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-matroska'
  ];
};

/**
 * Get file accept string for input element
 * @param {string} placementType - The placement type
 * @returns {string} Accept string for file input
 */
export const getFileAcceptString = (placementType) => {
  if (placementType === 'LED Screen') {
    return '.mp4,.mov,.avi,.mkv';
  }
  
  return '.jpg,.jpeg,.png,.webp,.mp4,.mov,.avi,.mkv';
};

/**
 * Extract file size in MB with 2 decimal places
 * @param {File} file - File object
 * @returns {string} File size formatted as "X.XX MB"
 */
export const extractFileSize = (file) => {
  if (!file || !file?.size) {
    return '0.00 MB';
  }
  
  const sizeInMB = file?.size / (1024 * 1024);
  return `${sizeInMB?.toFixed(2)} MB`;
};

/**
 * Get file type icon based on file extension or MIME type
 * @param {File|string} file - File object or filename/extension
 * @returns {string} Icon name for the file type
 */
export const getFileTypeIcon = (file) => {
  let fileType = '';
  
  if (typeof file === 'string') {
    fileType = file?.toLowerCase();
  } else if (file && file?.type) {
    fileType = file?.type?.toLowerCase();
  } else if (file && file?.name) {
    const extension = file?.name?.split('.')?.pop()?.toLowerCase();
    fileType = extension || '';
  }
  
  // Check for image types
  if (fileType?.includes('image') || 
      ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg']?.some(ext => fileType?.includes(ext))) {
    return 'Image';
  }
  
  // Check for video types
  if (fileType?.includes('video') || 
      ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv']?.some(ext => fileType?.includes(ext))) {
    return 'Video';
  }
  
  return 'File';
};

/**
 * Create fallback placeholder for missing or corrupted files
 * @param {number} width - Placeholder width (default: 200)
 * @param {number} height - Placeholder height (default: 150)
 * @returns {string} Data URL for placeholder image
 */
export const createFallbackPlaceholder = (width = 200, height = 150) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas?.getContext('2d');
  
  canvas.width = width;
  canvas.height = height;
  
  // Draw gray background
  ctx.fillStyle = '#f5f5f5';
  ctx?.fillRect(0, 0, width, height);
  
  // Draw border
  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 2;
  ctx?.strokeRect(1, 1, width - 2, height - 2);
  
  // Draw file icon (simplified)
  const iconSize = Math.min(width, height) * 0.3;
  const centerX = width / 2;
  const centerY = height / 2 - 10;
  
  ctx.fillStyle = '#9ca3af';
  ctx?.fillRect(centerX - iconSize/2, centerY - iconSize/2, iconSize, iconSize * 0.8);
  
  // Draw text
  ctx.fillStyle = '#6b7280';
  ctx.font = `${Math.max(12, width * 0.08)}px Arial`;
  ctx.textAlign = 'center';
  ctx?.fillText('No Preview', centerX, centerY + iconSize/2 + 20);
  
  return canvas?.toDataURL('image/png');
};